import { useEffect } from 'react';
import type { PendingUserMessage } from './AgentStreamDataContext';

/**
 * Schedule a timer per `'sending'` pending entry. When the entry's
 * `createdAt + timeoutMs` deadline elapses without it being removed
 * from the list (i.e. without an echoed user-message event), invoke
 * `onTimeout(clientId)`.
 *
 * Re-runs whenever `pendingMessages` changes; old timers are cleared
 * before new ones are scheduled so duplicates and orphans are
 * impossible. `onTimeout` should be stable (wrap in `useCallback` on
 * the caller side).
 */
export function usePendingEchoTimeout(
  pendingMessages: PendingUserMessage[],
  onTimeout: (clientId: string) => void,
  timeoutMs: number,
): void {
  useEffect(() => {
    const sending = pendingMessages.filter((p) => p.status === 'sending');
    if (sending.length === 0) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const now = Date.now();
    for (const p of sending) {
      const remaining = Math.max(0, p.createdAt + timeoutMs - now);
      timers.push(setTimeout(() => onTimeout(p.clientId), remaining));
    }
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [pendingMessages, onTimeout, timeoutMs]);
}
